import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import DocumentService from '../../Services/documents.service';
import ClientService from '../../Services/client.service';

export const fetchTransactionDocumentPageData = createAsyncThunk(
    'transactionDocumentPage/fetchData',
    async ({ type, caseId, date, startDate, endDate, isRange, exactDocumentDates, checkMonthClosed, token, requestKey, backgroundRefresh }, { rejectWithValue }) => {
        try {
            if (isRange) {
                const [response, closedResponse] = await Promise.all([
                    DocumentService.GetDocumentbyDateRange(type, caseId, startDate, endDate, token, { exactDocumentDates }),
                    checkMonthClosed
                        ? ClientService.CheckIfMonthClosed(caseId, startDate, token)
                        : Promise.resolve(null),
                ]);
                if (!response.ok) return rejectWithValue(await response.text());
                const json = await response.json();
                const documents = json.result || json;
                const isClosedReportingMonth = closedResponse?.ok ? await closedResponse.json() : false;
                return { documents, isClosedReportingMonth, requestKey, backgroundRefresh };
            } else {
                const [docResponse, closedResponse] = await Promise.all([
                    DocumentService.GetDocumentbyDate(type, caseId, date, token),
                    ClientService.CheckIfMonthClosed(caseId, date, token),
                ]);
                if (!docResponse.ok) return rejectWithValue(await docResponse.text());
                const documents = await docResponse.json();
                const isClosedReportingMonth = closedResponse.ok ? await closedResponse.json() : false;
                return { documents, isClosedReportingMonth, requestKey, backgroundRefresh };
            }
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    documents: [],
    isClosedReportingMonth: false,
    loading: false,
    error: null,
    lastFetchKey: null,
    expectedFetchKey: null,
};

const transactionDocumentPageSlice = createSlice({
    name: 'transactionDocumentPage',
    initialState,
    reducers: {
        resetTransactionDocumentPage: () => initialState,
        setTransactionDocuments: (state, action) => {
            state.documents = action.payload;
        },
        setTransactionMonthClosed: (state, action) => {
            state.isClosedReportingMonth = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTransactionDocumentPageData.pending, (state, action) => {
                const backgroundRefresh = action.meta?.arg?.backgroundRefresh;
                const hasExistingData = (state.documents?.length ?? 0) > 0;
                if (!(backgroundRefresh && hasExistingData)) state.loading = true;
                state.error = null;
                const key = action.meta?.arg?.requestKey;
                if (key != null) state.expectedFetchKey = key;
            })
            .addCase(fetchTransactionDocumentPageData.fulfilled, (state, action) => {
                const r = action.payload;
                const requestKey = r.requestKey;
                if (requestKey != null && state.expectedFetchKey != null && requestKey !== state.expectedFetchKey) {
                    state.loading = false;
                    return;
                }
                state.documents = Array.isArray(r.documents) ? r.documents : (r.documents?.result ?? []);
                state.isClosedReportingMonth = r.isClosedReportingMonth ?? false;
                state.loading = false;
            })
            .addCase(fetchTransactionDocumentPageData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetTransactionDocumentPage, setTransactionDocuments, setTransactionMonthClosed } = transactionDocumentPageSlice.actions;
export default transactionDocumentPageSlice.reducer;
